'use client';

import Markdown from "@/app/common/Markdown";
import { ChunkAST, ChunkDocContext, FootnoteDefinition } from "@/lib/parseMarkdown";
import { useEffect, useMemo, useRef, useState } from "react";
import { FootnoteReference } from "mdast";

type H2 = {
  offset: number;
  header: string;
  id: string;
  depth: number;
};

// Max rail entries before dropping a depth level: over this with
// h2s → h1 only; over with h3s (not h2s) → h1+h2; else all three.
const MAX_RAIL_ENTRIES = 18;

// Vertical room reserved per rail entry when spacing out close
// headings: a 2-line label (line-clamp-2 at leading-[1.05]) is ~32px.
const LABEL_BOX_HEIGHT = 32;

// Compress each heading into a short rail label (no "…"). Rules:
//   "Part 1. Verification Plan"              → "Part 1"
//   "1.1 Prepare Deal Optionality"           → "Prepare"
//   "2031-2036+ Improving the deal"          → "2031-2036+"
//   "2036+ Advanced robustness improvements" → "2036+"
//   "A.1 Setup" / "A.4.1 Backdoor..."        → "A.1" / "A.4.1"
//   "Appendix: Packet-Based Verification"    → "Appendix"
//   "April - May 2029: Third party..."       → "Apr-May 2029"
//   "Jan 2029: Mutual Chip..."               → "Jan 2029"
//   "2026: Chip tracking begins"             → "2026"
const MONTH_ABBR: Record<string, string> = {
  January: 'Jan', February: 'Feb', March: 'Mar', April: 'Apr',
  May: 'May', June: 'Jun', July: 'Jul', August: 'Aug',
  September: 'Sep', October: 'Oct', November: 'Nov', December: 'Dec',
};

function abbreviateMonths(s: string): string {
  return s
    .replace(
      /\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/g,
      (m) => MONTH_ABBR[m] ?? m,
    )
    // Compact ranges: "Apr - May 2029" → "Apr-May 2029"
    .replace(/\s+-\s+/g, '-');
}

function leftRailLabel(text: string): string {
  const trimmed = text.trim();
  const partMatch = trimmed.match(/^(Part\s+\d+)\b/);
  if (partMatch) return partMatch[1];
  // Author-citation headings: "Acemoglu (2024): …" / "Jones & Tonetti (2026): …" → first surname.
  const citeMatch = trimmed.match(/^([A-Z][^\s&(]+)(?:\s*&[^(]*)?\s*\(\d{4}\)/);
  if (citeMatch) return citeMatch[1];
  const numberedSec = trimmed.match(/^\d+\.\d+\s+(\S+)/);
  if (numberedSec) return numberedSec[1];
  const yearRangePlus = trimmed.match(/^(\d{4}-\d{4}\+?)\s+/);
  if (yearRangePlus) return yearRangePlus[1];
  const yearPlus = trimmed.match(/^(\d{4}\+)\s+/);
  if (yearPlus) return yearPlus[1];
  const appendixSec = trimmed.match(/^(A\.[\d.]*\d)\s+/);
  if (appendixSec) return appendixSec[1];
  if (/^Appendix\b/i.test(trimmed)) return 'Appendix';
  const colon = trimmed.indexOf(':');
  if (colon > 0 && /\d{4}/.test(trimmed.slice(0, colon))) {
    return abbreviateMonths(trimmed.slice(0, colon).trim());
  }
  return trimmed;
}

const elementOrEnclosingTable = (element: HTMLElement) => {
  const closestTable = element.closest('table');
  if (closestTable) {
    return closestTable;
  }
  return element;
}

const highlightLinkedElements = (identifier: string, highlight: boolean) => {
  // Several labels can exist for one footnote (prettytable's mobile/desktop
  // renders); highlight them all — the hidden ones don't paint anyway.
  const references = document.querySelectorAll(`[for="sn-${identifier}"]`);
  const footnote = document.getElementById(`footnote-${identifier}`);

  references.forEach((reference) => {
    if (highlight) {
      reference.classList.add('border-black/20');
    } else {
      reference.classList.remove('border-black/20');
    }
  });

  if (footnote) {
    if (highlight) {
      footnote.classList.add('border-black/20');
    } else {
      footnote.classList.remove('border-black/20');
    }
  }
};

const Sidenote = (
  footnotes: FootnoteDefinition[],
  activeMobileFootnote: string | null,
  setActiveMobileFootnote: (id: string | null) => void
) => {
  const FootnoteComponent = ({ footnoteReference }: { footnoteReference: FootnoteReference, docContext?: ChunkDocContext }) => {
    const definition = footnotes.find((f) => f.identifier === footnoteReference.identifier);
    if (!definition) {
      return null;
    }

    const handleFootnoteClick = (e: React.MouseEvent) => {
      e.preventDefault();
      setActiveMobileFootnote(
        activeMobileFootnote === footnoteReference.identifier ? null : footnoteReference.identifier
      );
    };

    return <>
      <label
        htmlFor={`sn-${footnoteReference.identifier}`}
        data-sidenote-number={footnoteReference.identifier}
        className="margin-toggle sidenote-number border-2 border-black/0 p-1/2 cursor-pointer"
        onClick={handleFootnoteClick}
        onMouseEnter={() => highlightLinkedElements(footnoteReference.identifier, true)}
        onMouseLeave={() => highlightLinkedElements(footnoteReference.identifier, false)}
      ></label>
      <input type="checkbox" id={`sn-${footnoteReference.identifier}`} className="margin-toggle" />
    </>;
  };

  FootnoteComponent.displayName = 'FootnoteComponent';
  return FootnoteComponent;
}

const setFootnotePositions = () => {
  const footnoteReferences = Array.from(document.querySelectorAll('label.sidenote-number')) as HTMLLabelElement[];
  const footnotes = Array.from(document.querySelectorAll('div[id^="footnote-"]')) as HTMLDivElement[];

  // `offsetTop` is relative to the nearest *positioned* ancestor, so
  // any wrapper with `position: relative` (e.g. `:::recs` <li>, table
  // cells) re-bases the number and parks the footnote at page top.
  // Use page-level coords anchored to the footnote's own offsetParent
  // so the `top` we set matches how the browser paints it.
  const pageY = (el: HTMLElement) =>
    el.getBoundingClientRect().top + window.scrollY;

  const footnotePositions = footnotes.map(footnote => {
    // A footnote can mount several reference labels: `:::prettytable`
    // renders its mobile cards and desktop grid as siblings (CSS shows one),
    // so anchor to a laid-out label — a display:none one has no client
    // rects and measures at page top. Fall back to the first label so
    // references inside closed <details> keep their hide/show handling.
    const candidates = footnoteReferences.filter(ref => ref.htmlFor.slice(3) === footnote.id.slice(9));
    const reference = candidates.find(ref => ref.getClientRects().length > 0) ?? candidates[0];
    const anchor = reference ? elementOrEnclosingTable(reference) : null;
    const containerOffset = footnote.offsetParent
      ? pageY(footnote.offsetParent as HTMLElement)
      : 0;
    const initialPosition = anchor ? pageY(anchor) - containerOffset : 0;
    return { footnote, reference, position: initialPosition };
  });

  footnotePositions.sort((a, b) => a.position - b.position);

  let adjusted = true;
  while (adjusted) {
    adjusted = false;
    for (let i = 1; i < footnotePositions.length; i++) {
      const prev = footnotePositions[i - 1];
      const current = footnotePositions[i];
      const minAllowed = prev.position + prev.footnote.offsetHeight + 10;
      if (current.position < minAllowed) {
        current.position = minAllowed;
        adjusted = true;
      }
    }
  }

  footnotePositions.forEach(({ footnote, reference, position }) => {
    const enclosingDetails = reference?.closest('details');
    footnote.style.top = `${position}px`;

    if (reference && !enclosingDetails) {
      footnote.classList.add('hidden', 'md:inline-block')
      return footnote.classList.remove('invisible');
    }
    if (enclosingDetails && !enclosingDetails.open) {
      return footnote.classList.add('hidden', 'md:hidden');
    }
    if (enclosingDetails && enclosingDetails.open) {
      footnote.classList.add('hidden', 'md:inline-block');
      return footnote.classList.remove('md:hidden', 'invisible');
    }
  });
}

const positionFootnotesAndTrackImages = () => {
  const images = Array.from(document.querySelectorAll('img'));

  setFootnotePositions();

  if (images.length === 0) {
    return;
  }

  // Add listeners to reposition footnotes whenever any image loads
  images.forEach(img => {
    if (!img.complete) {
      img.addEventListener('load', setFootnotePositions);
      img.addEventListener('error', setFootnotePositions);
    }
  });
}

export default function EssayPage({ nodes, toc = true, pageClassName, children, title, author, aboveTitle, railLabels }: { nodes: ChunkAST[], toc?: boolean, pageClassName?: string, children?: React.ReactNode, title?: string, author?: string, aboveTitle?: React.ReactNode, railLabels?: Record<string, string> }) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const mainRef = useRef<HTMLDivElement>(null);
  const [h2s, setH2s] = useState<H2[]>([]);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [activeMobileFootnote, setActiveMobileFootnote] = useState<string | null>(null);

  const footnotes: FootnoteDefinition[] = useMemo(() => nodes.length > 0 ? nodes[0].docContext.footnotes : [], [nodes]);

  // Sections positioned proportionally to their place in the text,
  // but with a min vertical gap so close ones don't overlap.
  const spacedH2s = useMemo(() => {
    if (h2s.length === 0 || viewportHeight === 0) return h2s;
    // Items render at `offset * 97%` of the track (≈ vh - 4px).
    const trackPx = Math.max(viewportHeight - 4, 1) * 0.97;
    const MIN_GAP_PX = LABEL_BOX_HEIGHT + 4; // 2-line label box + breathing room
    const minGap = MIN_GAP_PX / trackPx;
    // Every rail entry shows a 2-line label, so each needs the full gap.
    let lastOffset = -Infinity;
    return h2s.map(h2 => {
      const offset = Math.max(h2.offset, lastOffset + minGap);
      lastOffset = offset;
      return { ...h2, offset };
    });
  }, [h2s, viewportHeight]);

  useEffect(() => {
    // Dots are placed as a fraction of the *content div* (offset =
    // (heading.offsetTop - contentDiv.offsetTop) / contentDiv height;
    // see heading-scan effect). The fill must use the same basis or
    // it drifts from the dots — `<main>` is wrong (it also wraps the
    // title <header>, so its top/height don't match the dots').
    //
    // Measure page-Y via getBoundingClientRect to match the dots'
    // origin, and key off `window.scrollY` so a dot fills exactly
    // when its heading crosses the top of the page.
    const contentEl = mainRef.current?.querySelector(':scope > div.markdown-content') as HTMLElement | null;
    if (!contentEl) return;
    const contentTop = () => contentEl.getBoundingClientRect().top + window.scrollY;
    const handleScroll = () => {
      const height = contentEl.offsetHeight || 1;
      setScrollPosition((window.scrollY - contentTop()) / height);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      setScrollPosition(0);
    }
  }, []);

  useEffect(() => {
    const updateViewportHeight = () => setViewportHeight(window.innerHeight);
    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    return () => window.removeEventListener('resize', updateViewportHeight);
  }, []);

  useEffect(() => {
    // Include h1 (Parts), h2 (sub-sections), h3 (granular events) and h4
    // (subsection headings, e.g. Acemoglu/Jones/Bottlenecks) so the rail mirrors
    // the document structure.
    const allHeadings = mainRef.current?.querySelectorAll(':scope > div > :is(h1, h2, h3, h4)') as NodeListOf<HTMLHeadingElement>;
    if (!allHeadings) return
    const candidates = Array.from(allHeadings)
      // Appendix subheadings ("A.1", "A.2"…) stay off the rail; only
      // top-level "Appendix" shows. Numbered doc sections stay.
      .filter(h => !/^A\.\d/.test((h.textContent || '').trim()))
      .map(h => ({
      offset: (h.offsetTop - (h.parentElement?.offsetTop || 0)) / (h.parentElement?.offsetHeight || 1),
      header: railLabels?.[(h.textContent || '').trim()] ?? leftRailLabel(h.textContent || ''),
      id: h.id,
      depth: Number(h.tagName.slice(1)) || 2,
    }));
    // Pick the deepest level keeping entries <= MAX_RAIL_ENTRIES: drop
    // shallower (h1+h2, then h1) if deeper levels overflow, and allow h4
    // (e.g. Acemoglu/Jones/Bottlenecks) when they fit.
    const countUpTo = (d: number) => candidates.filter(h => h.depth <= d).length;
    const maxDepth =
      countUpTo(2) > MAX_RAIL_ENTRIES ? 1 :
      countUpTo(3) > MAX_RAIL_ENTRIES ? 2 :
      countUpTo(4) > MAX_RAIL_ENTRIES ? 3 :
      4;

    // Only keep headings at/above the chosen depth — each renders a
    // dot with its text label. Deeper headings (which would be
    // text-less dots) are dropped from the rail entirely.
    setH2s(candidates.filter(h => h.depth <= maxDepth));
  }, [railLabels]);

  useEffect(() => {
    positionFootnotesAndTrackImages();
    window.addEventListener('resize', setFootnotePositions);

    return () => {
      window.removeEventListener('resize', setFootnotePositions);
    }
  }, []);

  useEffect(() => {
    const detailsElements = mainRef.current?.querySelectorAll('details');
    if (!detailsElements) return

    const handleDetailsToggle = () => {
      setTimeout(setFootnotePositions, 10);
    };

    detailsElements.forEach(details => {
      details.addEventListener('toggle', handleDetailsToggle);
    });

    return () => {
      detailsElements.forEach(details => {
        details.removeEventListener('toggle', handleDetailsToggle);
      });
    };
  }, []);

  useEffect(() => {
    // Handle ESC key to close mobile footnote
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveMobileFootnote(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return <>

    {toc && (
      <div className="[grid-area:toc] hidden md:block">
        <nav className="section-nav sticky left-2 top-2 group">
          {/* Fluid-width rail: fills the toc grid column (capped at 220px) so
              labels wrap within the column instead of overflowing into the
              content when the column narrows. */}
          <div className="relative w-full max-w-[220px]" style={{ height: 'calc(100vh - 4px)' }}>
            {/* Full-height track: thin low-opacity line along the rail (same
                x-center and 97% scaling as the dots) so it reads as a
                continuous spine before scrolling. The thicker fill below
                paints over it as you scroll. */}
            <div className="absolute left-[2.5px] top-2 w-px bg-current opacity-15" style={{ height: 'calc(97% - 2px)' }} />
            {/* Fill height uses the same `* 97%` scaling as the dots (whose
                centers sit at offset * 97% + 6px) so the fill ends on the dot
                whose offset = scrollPosition. */}
            <div className="absolute left-[2px] top-2 w-[2px] bg-current" style={{ height: `calc(${Math.max(scrollPosition * 97, 2)}% - 2px)` }} />
            {spacedH2s.map(({ offset, header, id }) => (
              // Each row is vertically centered on its dot (top + translate)
              // so multi-line labels stay centered on the rail position.
              <div
                key={id}
                className="absolute w-full -translate-y-1/2 grid grid-cols-[14px_minmax(0,1fr)] items-center"
                style={{ top: `calc(${offset * 97}% + 6px)` }}
              >
                <a href={`#${id}`} className="justify-self-start">
                  <span className={`block size-[4px] ml-[1px] rounded-full border border-black ${offset <= scrollPosition ? 'bg-black' : 'bg-white'}`} />
                </a>
                <a
                  className="line-clamp-2 leading-[1.05] cursor-pointer text-black no-underline opacity-0 transition-opacity duration-300 group-hover:opacity-100 min-[1601px]:opacity-100"
                  href={`#${id}`}
                >{header}</a>
              </div>
            ))}
          </div>
        </nav>
      </div>
    )}

    <main className={`max-w-none [grid-area:content] supplement-content-shift ${pageClassName}`} ref={mainRef}>
      {aboveTitle}
      {title ? (
        <header className="pt-4 pb-8">
          <h1 className="w-full box-border max-w-none break-words text-5xl md:text-[70px] mt-0 mb-0">{title}</h1>
          {author ? (
            <h3 className="text-base md:text-xl mt-6 mb-0 font-normal text-gray-700">{author}</h3>
          ) : null}
        </header>
      ) : null}
      {/* good-ending-text so headings/prose match the playbook body
          (tufte's bare h1 is 68px; the playbook column overrides). */}
      <div className="good-ending-text markdown-content supplement-essay-prose w-full">
        <Markdown
          nodes={nodes.flatMap(chunk => chunk.nodes)}
          FootnoteHandler={Sidenote(footnotes, activeMobileFootnote, setActiveMobileFootnote)}
        />
        {children}
      </div>

      {activeMobileFootnote && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-4 border-t border-gray-300 z-50 md:hidden">
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold">Footnote {activeMobileFootnote}</span>
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={() => setActiveMobileFootnote(null)}
            >
              Close
            </button>
          </div>
          <div className="markdown-content footnote-mobile-content max-h-[40vh] overflow-y-auto">
            {footnotes.find(f => f.identifier === activeMobileFootnote)?.children && (
              <Markdown
                key={`footnote-${activeMobileFootnote}`}
                nodes={footnotes.find(f => f.identifier === activeMobileFootnote)?.children || []}
                FootnoteHandler={Sidenote(footnotes, activeMobileFootnote, setActiveMobileFootnote)}
              />
            )}
          </div>
        </div>
      )}
    </main>
    {/* max-[930px]:hidden — below the grid breakpoint that collapses the graph
        column to 0px, these absolutely-positioned sidenotes would hang past the
        page's right edge and (even while visibility:hidden) widen the layout
        viewport, making phones render the whole page zoomed out. Mobile uses
        the bottom-sheet footnote UI instead. */}
    <div className="[grid-area:graph] pointer-events-none max-[930px]:hidden">
      {footnotes.map(({ identifier, children }) => (
        <div
          id={`footnote-${identifier}`}
          key={`footnote-${identifier}`}
          className="markdown-content essay-footnote pointer-events-auto gap-2.5 absolute w-1/4 invisible transition-all duration-200 border-2 border-black/0 p-2 text-xs"
          onMouseEnter={() => {
            highlightLinkedElements(identifier, true);
          }}
          onMouseLeave={() => {
            highlightLinkedElements(identifier, false);
          }}
        >
          <span className="m-0 float-left mr-1 text-sm pt-[1px]">{identifier}.</span>
          <Markdown nodes={children} FootnoteHandler={Sidenote(footnotes, activeMobileFootnote, setActiveMobileFootnote)} />
        </div>
      ))}
    </div>
  </>
}
